import { useEffect, useMemo, type ReactNode } from 'react'
import { AddressContext } from './context'
import { applyAddressHead } from './head'
import { redirectTarget, tenantOf } from './model'
import type { AddressBoot } from './types'

/**
 * P12 — the gate every tree sits behind (main.tsx). Where the address says a
 * document belongs elsewhere (a renamed subdomain, a custom domain that is
 * or isn't the primary, the www door), it goes there with
 * `location.replace`, path, query and hash kept when `keep_path` says so,
 * and nothing renders. Otherwise it puts the company's icon and colour in
 * the head and gives the screens the address (useAddress).
 */
export function AddressGate({ boot, children }: { boot: AddressBoot; children: ReactNode }) {
  const target = useMemo(() => redirectTarget(boot, window.location), [boot])

  useEffect(() => {
    if (target !== null) {
      window.location.replace(target)
      return
    }

    const company = tenantOf(boot)
    if (company !== null) applyAddressHead(company)
  }, [boot, target])

  if (target !== null) return null

  return <AddressContext.Provider value={boot}>{children}</AddressContext.Provider>
}
